// components/dispense/ItemsSoldVatTable.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Individual items sold in a date range, each linking to its sale's
// product-level VAT breakdown. Sourced from /dispense/sales/items.
//
// Lives in the Finance & VAT module (VAT100 Box 1 drill-down); it used to sit
// on the Sales dashboard.

import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  ISaleItemsTotal,
  ISalesItem,
  ISalesItemsResponse,
} from '../../@types/dispense';
import { DispenseService } from '../../api/dispense.service';
import { fCurrency } from '../../utils/formatNumber';
import { fDate } from '../../utils/formatTime';
import { pluralize } from '../../utils/pluralize';
import { capitalizeFirstLetter } from '../../utils/util';
import Iconify from '../iconify';
import SaleVatBreakdown from './SaleVatBreakdown';

const dispenseService = new DispenseService();

/** Shows a genuine zero as £0.00 rather than the shared helper's blank. */
function fMoney(value: number | null | undefined) {
  return value ? fCurrency(value) : '£0.00';
}

/** Recorded and itemised figures closer than a penny count as matching. */
const PENNY = 0.005;

type SaleCheck =
  | { status: 'match' }
  | { status: 'unrecorded' }
  | { status: 'differs'; grossDiff: number; vatDiff: number };

/**
 * Compares a sale's item lines with the totals stored on the dispense. A
 * difference usually means add-ons/coatings (charged on the sale but not an
 * item line) or a sale edited after its lines were written.
 */
function checkSale(total: ISaleItemsTotal): SaleCheck {
  if (total.recordedGross === null) return { status: 'unrecorded' };
  const grossDiff = total.recordedGross - total.lineGross;
  const vatDiff = (total.recordedVat ?? 0) - total.lineVat;
  if (Math.abs(grossDiff) < PENNY && Math.abs(vatDiff) < PENNY) {
    return { status: 'match' };
  }
  return { status: 'differs', grossDiff, vatDiff };
}

/** Consecutive rows of the same sale (rows arrive ordered by sale). */
function groupBySale(rows: ISalesItem[]) {
  const groups: { dispenseId: number; rows: ISalesItem[] }[] = [];
  rows.forEach((row) => {
    const last = groups[groups.length - 1];
    if (last && last.dispenseId === row.dispenseId) last.rows.push(row);
    else groups.push({ dispenseId: row.dispenseId, rows: [row] });
  });
  return groups;
}

function SaleCheckChip({ total }: { total: ISaleItemsTotal }) {
  const check = checkSale(total);
  if (check.status === 'match') {
    return (
      <Tooltip title="The items add up to the gross and VAT recorded on this dispense.">
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<Iconify icon="eva:checkmark-circle-2-outline" />}
          label="Matches dispense"
        />
      </Tooltip>
    );
  }
  if (check.status === 'unrecorded') {
    return (
      <Tooltip title="This sale was recorded before VAT was captured on the dispense, so there is no stored total to compare with.">
        <Chip size="small" variant="outlined" label="No recorded total" />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={
        <>
          Recorded on dispense: gross {fMoney(total.recordedGross)}, VAT{' '}
          {fMoney(total.recordedVat)}. Items total: gross{' '}
          {fMoney(total.lineGross)}, VAT {fMoney(total.lineVat)}. Add-ons and
          coatings are charged on the sale but are not listed as items.
        </>
      }
    >
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        icon={<Iconify icon="eva:alert-triangle-outline" />}
        label={`Differs by ${fMoney(Math.abs(check.grossDiff))}`}
      />
    </Tooltip>
  );
}

type Props = {
  /** Inclusive YYYY-MM-DD bounds. */
  dateFrom: string;
  dateTo: string;
};

export default function ItemsSoldVatTable({ dateFrom, dateTo }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ISalesItemsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [vatSaleId, setVatSaleId] = useState<number | null>(null);

  // A new range starts from the first page.
  useEffect(() => {
    setPage(0);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dispenseService
      .getSalesItems({ dateFrom, dateTo, page: page + 1, limit: perPage })
      .then((res) => {
        if (!cancelled) setItems(res.data);
      })
      .catch((error: { message?: string }) => {
        if (!cancelled) {
          enqueueSnackbar(error?.message || 'Could not load items sold', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, page, perPage, enqueueSnackbar]);

  const groups = useMemo(() => groupBySale(items?.data ?? []), [items]);
  const saleTotalById = useMemo(
    () => new Map((items?.saleTotals ?? []).map((t) => [t.dispenseId, t])),
    [items],
  );
  const totals = items?.totals;

  return (
    <>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sold</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Sale</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">VAT</TableCell>
              <TableCell align="right">Exempt</TableCell>
              <TableCell align="right">VAT breakdown</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={32} />
                  <Skeleton variant="text" height={32} />
                </TableCell>
              </TableRow>
            )}

            {!loading && (items?.data.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No items sold in this period.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              groups.map((group) => {
                const saleTotal = saleTotalById.get(group.dispenseId);
                const partial =
                  saleTotal !== undefined &&
                  saleTotal.itemCount > group.rows.length;
                return (
                  <Fragment key={group.dispenseId}>
                    {group.rows.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setVatSaleId(item.dispenseId)}
                      >
                        <TableCell>
                          {item.soldAt ? fDate(item.soldAt, 'dd/MM/yyyy') : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.sku ? `${item.sku} · ` : ''}
                            {item.vatCategory
                              ? capitalizeFirstLetter(
                                  item.vatCategory.replace(/_/g, ' '),
                                )
                              : item.productType
                                ? capitalizeFirstLetter(item.productType)
                                : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`#${item.dispenseId}`}
                          />
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {fMoney(item.lineGross)}
                        </TableCell>
                        <TableCell align="right">
                          {fMoney(item.lineVat)}
                        </TableCell>
                        <TableCell align="right">
                          {fMoney(item.lineExempt)}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View this sale's VAT breakdown">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setVatSaleId(item.dispenseId);
                              }}
                            >
                              <Iconify icon="eva:file-text-outline" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {saleTotal && (
                      <TableRow
                        sx={{
                          bgcolor: 'background.neutral',
                          '& td': { fontWeight: 600 },
                        }}
                      >
                        <TableCell colSpan={3}>
                          <Typography variant="subtitle2">
                            Sale #{group.dispenseId} total
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pluralize(saleTotal.itemCount, 'item')}
                            {partial &&
                              ` · ${group.rows.length} shown on this page`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {saleTotal.quantity}
                        </TableCell>
                        <TableCell align="right">
                          {fMoney(saleTotal.lineGross)}
                        </TableCell>
                        <TableCell align="right">
                          {fMoney(saleTotal.lineVat)}
                        </TableCell>
                        <TableCell align="right">
                          {fMoney(saleTotal.lineExempt)}
                        </TableCell>
                        <TableCell align="right">
                          <SaleCheckChip total={saleTotal} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}

            {!loading && totals && totals.itemCount > 0 && (
              <TableRow
                sx={{
                  '& td': {
                    fontWeight: 700,
                    borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <TableCell colSpan={3}>
                  <Typography variant="subtitle2">Period total</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pluralize(totals.saleCount, 'sale')} ·{' '}
                    {pluralize(totals.itemCount, 'item')}
                  </Typography>
                </TableCell>
                <TableCell align="right">{totals.quantity}</TableCell>
                <TableCell align="right">{fMoney(totals.lineGross)}</TableCell>
                <TableCell align="right">{fMoney(totals.lineVat)}</TableCell>
                <TableCell align="right">{fMoney(totals.lineExempt)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={items?.totalRecords ?? 0}
        page={page}
        onPageChange={(_, next) => setPage(next)}
        rowsPerPage={perPage}
        rowsPerPageOptions={[10, 25, 50]}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog
        open={vatSaleId !== null}
        onClose={() => setVatSaleId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h6">Sale #{vatSaleId}</Typography>
              <Typography variant="body2" color="text.secondary">
                VAT per product, as charged at the point of sale
              </Typography>
            </Box>
            <IconButton onClick={() => setVatSaleId(null)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {vatSaleId !== null && (
            <SaleVatBreakdown dispenseId={vatSaleId} bare />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
